import { useState, useEffect } from 'react';
import { Zap, FileCode, AlertTriangle, CheckCircle } from 'lucide-react';
import { LoadingState, Spinner } from '@systemdox/ui';
import { docsApi, type DocIndex } from '@/services/docsApi';
import { aiApi, type ClaudeMdAnalysis } from '@/services/aiApi';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { MarkdownRenderer } from '@/components/docs/MarkdownRenderer';
import CollapsibleSection from './claude-config/CollapsibleSection';
import AgentConfigSection from './claude-config/AgentConfigSection';
import SettingsSection from './claude-config/SettingsSection';
import HooksSection from './claude-config/HooksSection';
import SkillsSection from './claude-config/SkillsSection';
import AgentsSection from './claude-config/AgentsSection';
import McpServersSection from './claude-config/McpServersSection';

export default function ClaudeMdTab() {
  const { selectedRepo } = useWorkspace();
  const [claudeFiles, setClaudeFiles] = useState<DocIndex[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<DocIndex | null>(null);
  const [content, setContent] = useState('');
  const [analysis, setAnalysis] = useState<ClaudeMdAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [claudeExists, setClaudeExists] = useState(false);

  useEffect(() => {
    setClaudeFiles([]);
    setSelectedDoc(null);
    setContent('');
    setAnalysis(null);
    setLoading(true);
    setClaudeExists(false);

    if (!selectedRepo) {
      setLoading(false);
      return;
    }

    const [owner, repo] = selectedRepo.split('/');

    docsApi.getFileContent(owner, repo, '.claude/CLAUDE.md')
      .then((result) => {
        if (result.content) {
          const virtualDoc: DocIndex = {
            id: `${selectedRepo}/.claude/CLAUDE.md`,
            repo_full_name: selectedRepo,
            path: '.claude/CLAUDE.md',
            title: 'CLAUDE.md',
            doc_type: 'claude_md',
            scenario_count: 0,
            size: result.content.length,
            synced_at: new Date().toISOString(),
          };
          setClaudeFiles([virtualDoc]);
          setSelectedDoc(virtualDoc);
          setContent(result.content);
          setClaudeExists(true);
        } else {
          setClaudeFiles([]);
        }
      })
      .catch(() => {
        setClaudeFiles([]);
      })
      .finally(() => setLoading(false));
  }, [selectedRepo]);

  async function selectDoc(doc: DocIndex) {
    setSelectedDoc(doc);
    setAnalysis(null);
    setContentLoading(true);
    try {
      const [owner, repo] = doc.repo_full_name.split('/');
      const result = await docsApi.getFileContent(owner, repo, doc.path);
      setContent(result.content);
    } finally {
      setContentLoading(false);
    }
  }

  async function handleAnalyze() {
    if (!selectedDoc) return;
    setAnalyzing(true);
    try {
      const result = await aiApi.analyzeClaudeMd(selectedDoc.repo_full_name, selectedDoc.path);
      setAnalysis(result);
    } finally {
      setAnalyzing(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Cross-agent emitters — one corpus, every agent the team uses */}
      <AgentConfigSection />

      {/* Provider heading */}
      <div className="flex items-center gap-3 pb-2 border-b border-slate-200 dark:border-slate-800">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
          <span className="text-white text-xs font-bold">C</span>
        </div>
        <div>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Claude Code</h2>
          <p className="text-[11px] text-slate-500">Project instructions, settings, hooks, skills, agents, and MCP</p>
        </div>
      </div>

      {/* CLAUDE.md — Analyze & Generate */}
      <CollapsibleSection
        title="CLAUDE.md"
        description="Analyze the project instructions committed to this repository"
        filePath=".claude/CLAUDE.md"
        icon={FileCode}
        exists={claudeExists}
        loading={loading}
        defaultOpen
      >
        <div className="space-y-6">
          {/* Analyze */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Analyze</h3>
            {claudeFiles.length === 0 ? (
              <p className="text-sm text-slate-500 py-2">
                No <span className="font-mono">.claude/CLAUDE.md</span> file found in this repository.
              </p>
            ) : (
              <div className="flex gap-4 border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden bg-white dark:bg-slate-950">
                {/* File list */}
                <div className="w-56 border-r border-slate-200 dark:border-slate-800 overflow-y-auto shrink-0">
                  <div className="p-1">
                    {claudeFiles.map((doc) => (
                      <button
                        key={doc.id}
                        onClick={() => selectDoc(doc)}
                        className={`w-full text-left px-3 py-2 rounded text-sm ${
                          selectedDoc?.id === doc.id
                            ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <FileCode className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">{doc.repo_full_name}</span>
                        </div>
                        <p className="text-xs text-slate-400 truncate ml-5.5">{doc.path}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Content + Analysis */}
                <div className="flex-1 min-w-0">
                  {selectedDoc && (
                    <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 dark:border-slate-800 shrink-0">
                      <span className="text-sm text-slate-500">{selectedDoc.repo_full_name}/{selectedDoc.path}</span>
                      <button
                        onClick={handleAnalyze}
                        disabled={analyzing}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
                      >
                        {analyzing ? <Spinner size="sm" tone="current" /> : <Zap className="w-3.5 h-3.5" />}
                        {analyzing ? 'Analyzing...' : 'Analyze with AI'}
                      </button>
                    </div>
                  )}

                  <div className="flex gap-4 p-4 max-h-96 overflow-y-auto">
                    <div className="flex-1 min-w-0">
                      {contentLoading ? (
                        <LoadingState size="md" />
                      ) : content ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <MarkdownRenderer content={content} />
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center mt-8">Select a CLAUDE.md file</p>
                      )}
                    </div>

                    {analysis && (
                      <div className="w-60 shrink-0 space-y-3">
                        <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Score</span>
                            <span className={`text-2xl font-bold ${
                              analysis.score >= 70 ? 'text-emerald-600' : analysis.score >= 40 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {analysis.score}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500">{analysis.summary}</p>
                        </div>

                        {analysis.missing_sections.length > 0 && (
                          <div className="p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
                            <h4 className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-2 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> Missing Sections
                            </h4>
                            <ul className="space-y-1">
                              {analysis.missing_sections.map((s, i) => (
                                <li key={i} className="text-xs text-amber-600 dark:text-amber-400">- {s}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {analysis.suggestions.length > 0 && (
                          <div className="p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
                            <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-2 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Suggestions
                            </h4>
                            <ul className="space-y-1">
                              {analysis.suggestions.map((s, i) => (
                                <li key={i} className="text-xs text-blue-600 dark:text-blue-400">- {s}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      </CollapsibleSection>

      {/* Settings & Permissions */}
      <SettingsSection />

      {/* Hooks */}
      <HooksSection />

      {/* Skills */}
      <SkillsSection />

      {/* Sub-agents */}
      <AgentsSection />

      {/* MCP Servers */}
      <McpServersSection />
    </div>
  );
}
